<script setup lang="ts">
import { onMounted, computed, ref, toRefs, watch } from 'vue';
import Calendar from 'primevue/calendar';
import InputMask from 'primevue/inputmask';
import OverlayPanel from 'primevue/overlaypanel';
import Listbox from 'primevue/listbox';
import { EMIT } from '@/enum';

interface PropsInterface {
  value: string[];
  disabled?: boolean;
}

const props = defineProps<PropsInterface>();

const { value, disabled } = toRefs(props);

const emit = defineEmits<{
  (e: EMIT.BLUR): void;
  (e: EMIT.CHANGE, value: Date[]): void;
}>();

const dateRanges = [
  { name: 'Today', id: 'T' },
  { name: 'Last 7 Days', id: 'LW' },
  { name: 'Last 28 Days', id: 'LM' },
  { name: 'Last 6 Months', id: 'LSM' },
  { name: 'Last 12 Months', id: 'LTM' },
  { name: 'Year-to-Date', id: 'YTD' },
  { name: 'Current School Year', id: 'CSY' },
  { name: 'Custom Range', id: 'CR' },
];

const selectedDateRange = ref({ name: 'Custom Range', id: 'CR' });

const dates = ref<Date[]>([]);

const firstDate = ref<Date>();

const secondDate = ref<Date>();

const formattedFirstDate = ref<string>('');

const formattedSecondDate = ref<string>('');

let dateFieldTimeout = 0;

const daterangepicker = ref();

onMounted(() => defineDates());

watch(dates, () => {
  if (!dates.value[0]) return;
  selectedDateRange.value = { name: 'Custom Range', id: 'CR' };
  updateDates();
});

watch(value, () => defineDates());

watch(selectedDateRange, () => {
  let newRange = selectedDateRange.value;

  if (newRange && newRange.id) {
    let today = new Date();

    switch (newRange.id) {
      case 'T':
        dates.value[0] = new Date();
        dates.value[1] = new Date();
        break;
      case 'LW':
        // This would be for setting to the last full week (M-F)
        // dates[0] = new Date(
        //   today.setDate(today.getDate() - ((today.getDay() + 6) % 7))
        // );
        // dates[1] = new Date(today.setDate(today.getDate() + 4));
        dates.value[0] = new Date(today.setDate(today.getDate() - 7));
        dates.value[1] = new Date();
        break;
      case 'LM':
        // This would be for setting to the last full month
        // dates[1] = new Date(today.setDate(0));
        // dates[0] = new Date(today.setDate(1));
        dates.value[0] = new Date(today.setDate(today.getDate() - 28));
        dates.value[1] = new Date();
        break;
      case 'LSM':
        dates.value[0] = new Date(today.setMonth(today.getMonth() - 6));
        dates.value[1] = new Date();
        break;
      case 'LTM':
        dates.value[0] = new Date(today.setMonth(today.getMonth() - 12));
        dates.value[1] = new Date();
        break;
      case 'YTD':
        today.setMonth(today.getMonth() - today.getMonth());
        today.setDate(1);
        dates.value[0] = new Date(today);
        dates.value[1] = new Date();
        break;
      case 'CSY':
        if (today.getMonth() <= 7) {
          today.setFullYear(today.getFullYear() - 1);
        }
        today.setMonth(7);
        today.setDate(1);
        dates.value[0] = new Date(today);
        dates.value[1] = new Date();
        break;
      default:
        break;
    }

    updateDates();
  }
});

const defaultDateRange = computed(() => {
  return firstDate.value
    ? firstDate.value.toLocaleDateString('en-US') +
        ' - ' +
        (secondDate.value ? secondDate.value.toLocaleDateString('en-US') : ' ')
    : null;
});

const defineDates = () => {
  if (value.value[0]) {
    dates.value[0] = new Date(value.value[0]);
    firstDate.value = new Date(dates.value[0]);
  }
  if (value.value[1]) {
    dates.value[1] = new Date(value.value[1]);
    secondDate.value = new Date(dates.value[1]);
  }
};

const toggleDateRangePicker = (event: Event) => {
  daterangepicker.value.toggle(event);
  emit(EMIT.BLUR);
};

const formatDate = (date: Date) => {
  let d = date.getDate();
  let dayString = d < 10 ? '0' + d : d.toString();
  let m = date.getMonth() + 1;
  let monthString = m < 10 ? '0' + m : m.toString();
  let y = date.getFullYear();

  return `${monthString}${dayString}${y}`;
};

const updateFromField = (field: number) => {
  if (dateFieldTimeout) clearTimeout(dateFieldTimeout);

  dateFieldTimeout = setTimeout(() => {
    let dateBeingUpdated = field === 0 ? formattedFirstDate.value : formattedSecondDate.value;

    if (!/[a-z]/i.test(dateBeingUpdated)) {
      dates.value[field] = new Date(dateBeingUpdated);
    }
  }, 500);
};

const updateDates = () => {
  formattedFirstDate.value = dates.value[0] ? formatDate(dates.value[0]) : '';
  formattedSecondDate.value = dates.value[1] ? formatDate(dates.value[1]) : '';
};

const applyDates = () => {
  firstDate.value = dates.value[0];
  secondDate.value = dates.value[1];
  emit(EMIT.CHANGE, dates.value);
  emit(EMIT.BLUR);
  daterangepicker.value.hide();
};
</script>

<template>
  <div class="daterangepicker-container">
    <IconField icon-position="left">
      <InputIcon class="pi p-input-icon-left">
        <Icon name="calendar_today" class="text-lg z-1" />
      </InputIcon>
      <InputText
        class="w-full"
        :value="defaultDateRange"
        :disabled="disabled"
        placeholder="Select a Date"
        @click="toggleDateRangePicker"
      />
    </IconField>
    <OverlayPanel ref="daterangepicker" @hide="() => emit(EMIT.BLUR)">
      <div class="daterangepicker-section">
        <Calendar id="basic" v-model="dates" selection-mode="range" :inline="true" />
        <div class="daterangepicker-sidebar">
          <Listbox v-model="selectedDateRange" :options="dateRanges" option-label="name" />
        </div>
      </div>
      <div class="daterangepicker-footer">
        <InputMask
          v-model="formattedFirstDate"
          type="text"
          mask="99/99/9999"
          placeholder="xx/xx/xxxx"
          slot-char="mm/dd/yyyy"
          :auto-clear="false"
          @keyup="updateFromField(0)"
        />
        -
        <InputMask
          v-model="formattedSecondDate"
          type="text"
          mask="99/99/9999"
          placeholder="xx/xx/xxxx"
          slot-char="mm/dd/yyyy"
          :auto-clear="false"
          @keyup="updateFromField(1)"
        />
        <AppButton text="Cancel" type="secondary" @click="toggleDateRangePicker" />
        <AppButton text="Apply" @click="applyDates" />
      </div>
    </OverlayPanel>
  </div>
</template>

<style scoped lang="scss">
::v-deep(.p-datepicker) {
  border: none;
  padding: 0px;
  .p-datepicker-header {
    border-bottom-width: 3px;
    border-bottom-color: var(--surface-100);
  }
}

.daterangepicker-container {
  display: flex;
  flex-direction: column;
  position: relative;
  max-width: 360px;
}
</style>
